import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Alert,
  Chip,
  Skeleton,
} from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import { toast, ToastContainer } from "react-toastify";
import { listCourses } from "../../api/assessmentHierarchy";
import {
  LEADERBOARD_SCOPES,
  getLeaderboard,
  apiErrorMessage,
} from "../../api/assessmentLeaderboard";

const ACCENT = "#17479e";

const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1).replace(/\.0$/, "")}%`);

const MEDALS = {
  1: { ring: "#f2c53d", badge: "#e0a614", label: "Gold" },
  2: { ring: "#b9c2cf", badge: "#8a97a8", label: "Silver" },
  3: { ring: "#e0a568", badge: "#c1732f", label: "Bronze" },
};

const initials = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

function PodiumAvatar({ student, rank }) {
  const medal = MEDALS[rank];
  const isFirst = rank === 1;
  return (
    <Box sx={{ textAlign: "center", minWidth: isFirst ? 96 : 84 }}>
      <Box sx={{ height: 28, display: "flex", alignItems: "flex-end", justifyContent: "center", mb: 0.5 }}>
        {isFirst && <EmojiEventsOutlinedIcon sx={{ color: "#e0a614", fontSize: 28 }} />}
      </Box>
      <Avatar
        sx={{
          width: isFirst ? 84 : 68,
          height: isFirst ? 84 : 68,
          mx: "auto",
          fontWeight: 700,
          fontSize: isFirst ? "1.3rem" : "1.05rem",
          backgroundColor: "#eef1f6",
          color: ACCENT,
          border: `4px solid ${medal.ring}`,
          boxShadow: `0 6px 16px ${medal.ring}66`,
        }}
      >
        {initials(student.studentName)}
      </Avatar>
      <Box
        sx={{
          width: 30,
          height: 30,
          mx: "auto",
          mt: -2,
          position: "relative",
          zIndex: 1,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: medal.badge,
          color: "#fff",
          fontWeight: 800,
          fontSize: "0.85rem",
          border: "3px solid #fff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        }}
      >
        {rank}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: isFirst ? "1.05rem" : "0.95rem", mt: 1 }}>
        {student.studentName || "—"}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: isFirst ? "1.4rem" : "1.15rem", color: ACCENT }}>
        {fmtPct(student.avgPercentage)}
      </Typography>
    </Box>
  );
}

const headerCellSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "text.secondary",
  letterSpacing: 0.5,
};

function AssessmentLeaderboard() {
  const [scope, setScope] = useState("ALL");
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [entries, setEntries] = useState([]);
  const [myEntry, setMyEntry] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listCourses({ includeInactive: false })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [scope, courseId, rowsPerPage]);

  useEffect(() => {
    if (scope === "COURSE" && !courseId) {
      setEntries([]);
      setMyEntry(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getLeaderboard({
      scope,
      courseId: scope === "COURSE" ? courseId : undefined,
      page,
      size: rowsPerPage,
    })
      .then((res) => {
        if (cancelled) return;
        setEntries(res.entries);
        setMyEntry(res.myEntry);
      })
      .catch((err) => {
        if (cancelled) return;
        setEntries([]);
        setMyEntry(null);
        toast.error(apiErrorMessage(err, "Failed to load leaderboard"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope, courseId, page, rowsPerPage]);

  // The podium only makes sense on the first page, where ranks 1-3 are present.
  const podium = page === 0 ? entries.filter((e) => e.rank <= 3) : [];
  const [first, second, third] = [1, 2, 3].map((r) => podium.find((e) => e.rank === r));
  const rest = entries.filter((e) => !(page === 0 && e.rank <= 3));

  // The backend returns no total; a full page means there may be another one.
  const count = entries.length === rowsPerPage ? -1 : page * rowsPerPage + entries.length;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => setRowsPerPage(parseInt(event.target.value, 10));

  const emptyMessage =
    scope === "COURSE" && !courseId
      ? "Select an exam to see its leaderboard"
      : scope === "COLLEGE"
        ? "No rankings found. This view lists students from the signed-in user's own college, so it is empty for accounts without one."
        : "No rankings found. Students need at least 3 completed attempts to be ranked.";

  const isMe = (row) => myEntry && row.studentUserId === myEntry.studentUserId;

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />
      <Card sx={{ boxShadow: 1, mb: 3 }}>
        <CardContent sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Scope</InputLabel>
            <Select label="Scope" value={scope} onChange={(e) => setScope(e.target.value)}>
              {LEADERBOARD_SCOPES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {scope === "COURSE" && (
            <FormControl size="small" sx={{ minWidth: 240 }} required>
              <InputLabel>Exam</InputLabel>
              <Select label="Exam *" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </CardContent>
      </Card>

      {scope === "COLLEGE" && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This view ranks students from the same college as the signed-in user.
        </Alert>
      )}

      {myEntry && (
        <Alert severity="success" icon={false} sx={{ mb: 3 }}>
          Your rank: <b>#{myEntry.rank}</b> · {fmtPct(myEntry.avgPercentage)} score ·{" "}
          {fmtPct(myEntry.avgAccuracy)} accuracy
        </Alert>
      )}

      {first && (
        <Paper
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "#f7f8fb",
            p: { xs: 3, md: 4 },
            mb: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            gap: { xs: 1.5, sm: 3, md: 5 },
            flexWrap: "wrap",
          }}
        >
          {second && <PodiumAvatar student={second} rank={2} />}
          <Box sx={{ transform: { md: "translateY(-16px)" } }}>
            <PodiumAvatar student={first} rank={1} />
          </Box>
          {third && <PodiumAvatar student={third} rank={3} />}
        </Paper>
      )}

      <Paper sx={{ borderRadius: 3, boxShadow: 2, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            px: 2.5,
            py: 1.25,
            backgroundColor: "#fafbfc",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ flex: "0 0 60px", ...headerCellSx }}>RANK</Typography>
          <Typography sx={{ flex: 1, ...headerCellSx }}>STUDENT</Typography>
          <Typography sx={{ flex: "0 0 100px", textAlign: "right", ...headerCellSx }}>SCORE</Typography>
          <Typography sx={{ flex: "0 0 100px", textAlign: "right", ...headerCellSx }}>ACCURACY</Typography>
        </Box>

        {loading ? (
          <Box sx={{ px: 2.5, py: 1.5 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={44} />
            ))}
          </Box>
        ) : rest.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {first ? "No more rankings" : emptyMessage}
            </Typography>
          </Box>
        ) : (
          rest.map((row, i) => (
            <Box
              key={`${row.rank}-${row.studentUserId}`}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2.5,
                py: 1.5,
                borderBottom: i < rest.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
                backgroundColor: isMe(row) ? "#e8f0fe" : "transparent",
                "&:hover": { backgroundColor: isMe(row) ? "#e8f0fe" : "rgba(0,0,0,0.02)" },
              }}
            >
              <Typography sx={{ flex: "0 0 60px", fontWeight: 700, color: "text.secondary" }}>{row.rank}</Typography>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    backgroundColor: "#eef1f6",
                    color: ACCENT,
                    fontWeight: 700,
                    fontSize: "0.8rem",
                  }}
                >
                  {initials(row.studentName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }} noWrap>
                    {row.studentName || "—"}
                    {isMe(row) && (
                      <Chip label="You" size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {row.collegeName || "—"}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ flex: "0 0 100px", textAlign: "right", fontWeight: 800, color: ACCENT }}>
                {fmtPct(row.avgPercentage)}
              </Typography>
              <Typography sx={{ flex: "0 0 100px", textAlign: "right", color: "text.secondary" }}>
                {fmtPct(row.avgAccuracy)}
              </Typography>
            </Box>
          ))
        )}

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>
    </Box>
  );
}

export default AssessmentLeaderboard;
